import {track, type FunnelEventMap} from '@core/utils/analytics'

// Ovid analytics — migrated from the old PackModelEventMap taxonomy to the shared
// FunnelEventMap (ovid funnel port, Stage 1; mirrors narcis's own migration —
// products/narcis/utils/analytics.ts's NarcisEventMap). `FunnelEventMap` REPLACES
// `PackModelEventMap` on the surfaces this stage touches (pack pages: PackLandingTemplate,
// PackHeroStage) — the dictionary
// (thinking/design/handoffs/funnel/docs/2026_07_24_funnel_spec.md §2) is now what ovid's pack
// surfaces speak. `sign_up` is omitted from the intersection for the same reason narcis's own map
// omits it (it never fires through `ovidTrack` — see FunnelEventMap's own header comment for
// where it does).
//
// Untouched-by-Stage-1 surfaces (`/muse`, `/model` intake, hub, emails — Stages 2-5) still import
// this same module; their call sites are unaffected until those stages land.
export type OvidEventMap = Omit<FunnelEventMap, 'sign_up'> & {
    // Auth gate hit — shared cross-cutting infra kept declared past the PackModelEventMap
    // retirement (mirrors NarcisEventMap's own gate_hit re-declaration).
    gate_hit: { gate: string; trigger: string; packSlug?: string }
    // Face slots restored from sessionStorage — shared cross-cutting infra, same rationale.
    face_slots_restored: { slot_count: number; restore_source: 'pack' | 'portrait' | 'global'; packSlug?: string }
    // 18+ entry overlay (plan 2026_08_05_ovid_age_gate.md, step 6). Distinct keys rather than a
    // widening of core `gate_hit` — the core map stays cross-product, and only ovid has an age
    // gate. Params follow gate_hit's shape so the two read alike in GA4.
    age_gate_shown: { gate: 'age'; trigger: 'entry' }
    age_gate_passed: { gate: 'age'; trigger: 'entry' }
    // /model entry attribution. 'your_model_block' (MuseBlock) and
    // 'userblock_dropdown' (UserBlock) are live call sites, untouched here. 'pack_gate' — the pack
    // page's train-CTA — had no call site through the funnel port (dropped on the theory that no
    // dictionary event covered the moment, the same call narcis's own migration made); RESTORED
    // 2026-08-11 mirroring narcis's re-wire (DEVIATIONS.md #106): PackHeroStage.tsx's
    // handleCTAClick fires it from every branch that navigates to /model with nothing downstream to
    // report the tap.
    model_cta_click: { source: 'two_times_strip' | 'your_model_block' | 'userblock_dropdown' | 'pack_gate' | 'muse_page' | 'muse_strip' }
    // Pack hub events (2026-07-17 pack hub unification) — untouched by this stage, kept declared.
    pack_hub_search: { query: string }
    pack_hub_category_selected: { category: string }
    pack_hub_sort_changed: { sort: string }
    pack_hub_load_more: { page: number }
    pack_card_clicked: { packSlug: string; surface: 'hub' | 'home'; category: string; position: number }
    pack_featured_clicked: { packSlug: string; position: number }
    // consent-required added Stage 3 (ovid funnel port, intake consent attestation) — the fourth
    // refusal reason `_layout.model.tsx`'s start-training action can now return.
    model_training_refused: { reason: 'already-training' | 'invalid-photo-count' | 'consent-required' | 'photos-unavailable' | 'name-required' | 'subject-not-woman' | 'payment-required' | 'plan-required' }
    // Paid muse (Narcis's pay-at-launch events, same names): the muse checkout opened; the paid Start confirmed, once
    // per transaction (`transaction_id` is the processor's payment reference); the buyer left Checkout. The price is
    // not known to the page, so `checkout_started` carries the kind, not a value.
    checkout_started: { kind: 'muse' | 'plan' }
    purchase: { transaction_id: string; value: number; currency: string }
    checkout_cancelled: Record<string, never>
    model_deleted: Record<string, never>
    model_outcome_observed: { status: 'ready' | 'failed'; photo_count: number }
    // Muses (2026-08-23, plan 2026_08_23_ovid_muses.md). `slug` is null for "Leave it to chance".
    // Declared here for the chooser UI landing in Step 2 to call — the `choose-muse` server action
    // (Step 1, `_layout.muse.tsx`) does not fire this itself: `ovidTrack`/`track()` guards on
    // `typeof window`, so a server-side call would be a silent no-op (see this file's own header
    // comment — "There is no server-side GA4 emitter in this codebase" is core/utils/analytics.ts's
    // documented posture). The real fire point is client-side, after the chooser's fetcher resolves.
    // `source` (2026-08-25, muse process coverage pass) — which of the three surfaces that embed
    // the chooser fetcher resolved this pick: PackLandingTemplate's inline pick step
    // ('pack_page'), the /muse grid itself ('muse_page'), or the /muse/{slug} detail page's own
    // "Make her your muse" button ('muse_detail'). All three call the same `choose-muse` action, so
    // without this the three populations were indistinguishable in GA4. 'wall' added 2026-09-17
    // (plan `2026_09_17_ovid_wall_verbs.md` § Decided 5/6) for the fourth surface, `MuseStrip` on
    // the signed-in wall's column/sheet — a fifth call site to the SAME shape, `POST /mine/muse`
    // rather than the `/muse` route's `choose-muse` action, but the one event the funnel already
    // reads for every other chooser surface.
    muse_chosen: { slug: string | null; source: 'pack_page' | 'muse_page' | 'muse_detail' | 'wall' }
    // Every link/button that leads INTO the chooser without itself completing a pick (2026-08-25,
    // muse process coverage pass) — the funnel step before `muse_chosen`, so a chooser view with no
    // resulting pick is now visible as a drop-off instead of vanishing between the click and the
    // fetcher resolution. `source` names the surface the click originated from, not a destination
    // (every value routes to /muse or /muse/{slug}):
    //   'userblock'        — the header avatar's ambient "Your muse" links and its dropdown's
    //                         unconditional /muse item (UserBlock.tsx, flag-off world)
    //   'hub_band'         — the /packs hub's "Change muse →" / "Choose your muse" band
    //   'pack_panel_all'   — the pack page's anon musePanel "All 32 →" link
    //   'pack_row_change'  — the pack page's rowMuse "Change muse →" link, and MusePickStep's own
    //                        "All 32 →" (the sign-in → pick flow's step 2 header link — same
    //                        onward-to-the-full-roster intent as the row's Change link)
    //   'muse_detail_all'  — the /muse/{slug} page's "Other muses" column "All 32 muses →"
    //   'muse_page_change' — MuseBlock's "Choose another muse" (the /muse after-choice board)
    muse_nav_click: {
        source: 'userblock' | 'hub_band' | 'pack_panel_all' | 'pack_row_change' | 'muse_detail_all' | 'muse_page_change'
    }
    home_hero_cta_click: { destination: string; label: string; tier: 'primary' | 'ghost' | 'text' }
    upload_compress_failed: {
        stage: 'offscreen_canvas' | 'canvas_2d' | 'image_bitmap'
        error: string
        has_offscreen_canvas: boolean
        has_create_image_bitmap: boolean
        user_agent: string
    }
}

// Straight passthrough to the shared core track() — same shape as narcisTrack. Routing through
// track() (rather than its own ReactGA.event() + isTrackingEnabled() copy) means ovidTrack shares
// the exact same plumbing narcis and quies use, so it picks up the browser_session_id GA4 bridge
// (@core/utils/analytics's setBrowserSessionId, set as a user property from app/root.tsx)
// automatically, with no ovid-specific code — a bridge ovid never had before 2026-07-16.
export function ovidTrack<E extends keyof OvidEventMap>(event: E, params: OvidEventMap[E]): void {
    track(event as any, params as any)
}
